// src/hooks/useDetectorRelations.ts
import { useEffect, useState, useMemo } from "react";

// --- Local Development Mode ---
// Set to true to proxy all API calls to localhost:8000
const LOCAL_MODE = false;

function apiUrl(path: string): string {
    return LOCAL_MODE ? `http://localhost:8000${path}` : path;
}

function credentials(): RequestCredentials {
    return LOCAL_MODE ? "include" : "same-origin";
}

// --- Interfaces (can be imported from a shared types file) ---
interface DetectorObject {
    mac: string;
    ip: string;
    last_active: string;
    online: boolean;
    status: {
        site_name: string;
        gps_fix: boolean;
        gps_lat: number;
        gps_long: number;
        up_since: string;
        memory: string;
        fw_version: string;
        test_mode: boolean;
        thr: string;
        linux: boolean;
        sim_card_no?: string;
        dacq_algo: string | boolean,
        peak_algo: string | boolean,
        csv_algo: string | boolean,
        file_algo: string | boolean
    };
}

interface DetectorCodeObject {
    code: string;
    coeff: string;
    created_at: string;
    expiration_date: string;
    id: string;
    lat: string;
    lon: string;
    name: string;
    receiver: string;
    reg: string;
    sim_card: string;
}

interface SubAssemblyPart {
    part: number;
    quantity_required: number;
}

interface SubAssembly {
    id: number;
    name: string;
    parts: SubAssemblyPart[];
    subPicture?: any;
    subSOP?: any;
}

interface DetectorSubAssembly {
    subassembly: number | SubAssembly;
    quantity_required: number;
}

interface Detector {
    id: number;
    name: string;
    version: string;
    subassemblies: DetectorSubAssembly[];
}

interface BuiltSubAssemblies {
    id: string;
    built_at: string;
    built_by: string;
    status: string;
    subassembly: number;
    used_by: string;
}

interface BuiltDetectors {
    mac_address: string;
    code?: string;
    built_at: string;
    assembled_by: string | number;
    status: string;
    detector: number | null;
    subassemblies: string[];
}

interface PlannedDetectorLocation {
    loc_id: number;
    location_name: string;
    region?: string | null;
    address?: string | null;
    county?: string | null;
    latitude: number;
    longitude: number;
    planned_deployment_date?: string | null;
    contact_person?: string | null;
    contact_email?: string | null;
    contact_number?: string | null;
    note?: string | null;
    is_authorized: boolean;
    is_deployed: boolean;
}

// Helper function to normalize MAC addresses
function normalizeMac(mac: string): string {
    return mac?.toLowerCase().replace(/^(0x|000)/, "") || "";
}

// --- Hook Implementation ---
export function useDetectorRelations() {
    // Raw data
    const [detectorDataMonitoring, setDetectorDataMonitoring] = useState<DetectorObject[]>([]);
    const [detectorCodeObj, setDetectorCodeObj] = useState<DetectorCodeObject[]>([]);
    const [subAssemblies, setSubAssemblies] = useState<SubAssembly[]>([]);
    const [detectors, setDetectors] = useState<Detector[]>([]);
    const [builtDetectors, setBuiltDetectors] = useState<BuiltDetectors[]>([]);
    const [builtSubAssemblies, setBuiltSubAssemblies] = useState<BuiltSubAssemblies[]>([]);
    const [lastFetched, setLastFetched] = useState<Date | null>(null);
    const [plannedLocations, setPlannedLocations] = useState<PlannedDetectorLocation[]>([]);

    // ---- Fetchers ----
    useEffect(() => {
        const fetchMonitoring = async () => {
            try {
                const response = await fetch(apiUrl("/apidet/detector/monitoring"), { credentials: credentials() });
                if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
                const data = await response.json();

                let alljson: any = {};
                if (data.dataUSA && !data.dataUSA.error) {
                    const cleanedString = data.dataUSA.replace(/\\n/g, "").trim();
                    alljson = JSON.parse(cleanedString);
                }

                const combined = [
                    ...Object.values(alljson).flat(),
                ].filter((d): d is DetectorObject => !!d && typeof d === "object" && "mac" in d);

                setDetectorDataMonitoring(combined);
                setLastFetched(new Date());
            } catch (err) {
                console.error("Error fetching monitoring:", err);
                setDetectorDataMonitoring([]);
            }
        };

        fetchMonitoring();
        const interval = setInterval(fetchMonitoring, 5000);
        return () => clearInterval(interval);
    }, []);

    const fetchRelayWithBackup = async () => {
        const ONE_HOUR_MS = 60 * 60 * 1000;

        try {
            const res = await fetch(apiUrl("/portal/api/relay_fnn_network"), {
                credentials: credentials()
            });

            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            if (!Array.isArray(data)) throw new Error("Non-array response");

            setDetectorCodeObj(data);

            try {
                const backupRes = await fetch(apiUrl("/portal/api/relay_fnn_network_backup"), {
                    credentials: credentials()
                });
                if (backupRes.ok) {
                    const backup = await backupRes.json();
                    const lastWritten = backup._backup_timestamp
                        ? new Date(backup._backup_timestamp).getTime()
                        : 0;
                    const isStale = Date.now() - lastWritten > ONE_HOUR_MS;

                    if (isStale) {
                        const csrfRes = await fetch(apiUrl("/api/csrf-token/"), {
                            credentials: credentials()
                        });
                        const csrfData = await csrfRes.json();
                        const csrfToken = csrfData.csrfToken;

                        await fetch(apiUrl("/portal/api/relay_fnn_network_backup/update/"), {
                            method: "POST",
                            credentials: credentials(),
                            headers: {
                                "Content-Type": "application/json",
                                "X-CSRFToken": csrfToken,
                            },
                            body: JSON.stringify({ data }),
                        });
                    }
                }
            } catch (backupErr) {
                console.warn("Could not update relay backup:", backupErr);
            }

        } catch (err) {
            console.warn("Live relay endpoint offline, falling back to backup:", err);
            try {
                const backupRes = await fetch(apiUrl("/portal/api/relay_fnn_network_backup/"), {
                    credentials: credentials()
                });
                if (backupRes.ok) {
                    const backup = await backupRes.json();
                    if (Array.isArray(backup.entries) && backup.entries.length > 0) {
                        setDetectorCodeObj(backup.entries);
                    }
                }
            } catch (backupErr) {
                console.error("Relay backup also unavailable:", backupErr);
            }
        }
    };

    useEffect(() => {
        fetchRelayWithBackup();

        fetch(apiUrl("/api/inventory_subassemblies/"), { credentials: credentials() })
            .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
            .then((data) => { if (Array.isArray(data)) setSubAssemblies(data); })
            .catch((err) => console.error("Error fetching subassemblies:", err));

        fetch(apiUrl("/api/inventory_detectors/"), { credentials: credentials() })
            .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
            .then((data) => { if (Array.isArray(data)) setDetectors(data); })
            .catch((err) => console.error("Error fetching detectors:", err));

        fetch(apiUrl("/api/inventory_built_subs/"), { credentials: credentials() })
            .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
            .then((data) => { if (Array.isArray(data)) setBuiltSubAssemblies(data); })
            .catch((err) => console.error("Error fetching built subassemblies:", err));

        fetch(apiUrl("/api/inventory_built_dets/"), { credentials: credentials() })
            .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
            .then((data) => { if (Array.isArray(data)) setBuiltDetectors(data); })
            .catch((err) => console.error("Error fetching built detectors:", err));

        fetch(apiUrl("/api/detector_planned_locations/"), { credentials: credentials() })
            .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
            .then((data) => { if (Array.isArray(data)) setPlannedLocations(data); })
            .catch((err) => console.error("Error fetching planned detector locations:", err));

    }, []);

    // Refresh function to manually refetch all data
    const refreshDetectors = async () => {
        try {
            const monitoringRes = await fetch(apiUrl("/apidet/detector/monitoring"), { credentials: credentials() });
            if (monitoringRes.ok) {
                const data = await monitoringRes.json();
                let alljson: any = {};
                if (data.dataUSA && !data.dataUSA.error) {
                    const cleanedString = data.dataUSA.replace(/\\n/g, "").trim();
                    alljson = JSON.parse(cleanedString);
                }
                const combined = [
                    ...Object.values(alljson).flat(),
                ].filter((d): d is DetectorObject => !!d && typeof d === "object" && "mac" in d);
                setDetectorDataMonitoring(combined);
            }

            const [, subsRes, detsRes, builtSubsRes, builtDetsRes, plannedLocsRes] = await Promise.all([
                fetchRelayWithBackup(),
                fetch(apiUrl("/api/inventory_subassemblies/"), { credentials: credentials() }),
                fetch(apiUrl("/api/inventory_detectors/"), { credentials: credentials() }),
                fetch(apiUrl("/api/inventory_built_subs/"), { credentials: credentials() }),
                fetch(apiUrl("/api/inventory_built_dets/"), { credentials: credentials() }),
                fetch(apiUrl("/api/detector_planned_locations/"), { credentials: credentials() })
            ]);

            if (subsRes.ok) { const d = await subsRes.json(); if (Array.isArray(d)) setSubAssemblies(d); }
            if (detsRes.ok) { const d = await detsRes.json(); if (Array.isArray(d)) setDetectors(d); }
            if (builtSubsRes.ok) { const d = await builtSubsRes.json(); if (Array.isArray(d)) setBuiltSubAssemblies(d); }
            if (builtDetsRes.ok) { const d = await builtDetsRes.json(); if (Array.isArray(d)) setBuiltDetectors(d); }
            if (plannedLocsRes.ok) { const d = await plannedLocsRes.json(); if (Array.isArray(d)) setPlannedLocations(d); }

        } catch (err) {
            console.error("Error refreshing detector data:", err);
        }
    };

    // ---- Derived / Connected Relationships ----
    const fullDetectors = useMemo(() => {
        const safeDetectorCodeObj = Array.isArray(detectorCodeObj) ? detectorCodeObj : [];
        const safeDetectorDataMonitoring = Array.isArray(detectorDataMonitoring) ? detectorDataMonitoring : [];
        const safeBuiltDetectors = Array.isArray(builtDetectors) ? builtDetectors : [];
        const safeBuiltSubAssemblies = Array.isArray(builtSubAssemblies) ? builtSubAssemblies : [];
        const safeDetectors = Array.isArray(detectors) ? detectors : [];
        const safeSubAssemblies = Array.isArray(subAssemblies) ? subAssemblies : [];

        const latestCodes = Object.values(
            safeDetectorCodeObj.reduce((acc, c) => {
                const receiverKey = normalizeMac(c.receiver);
                acc[receiverKey] = c;
                return acc;
            }, {} as Record<string, DetectorCodeObject>)
        );

        return safeDetectorDataMonitoring.map((mon) => {
            const normalizedMonMac = normalizeMac(mon.mac);
            const codeEntry = latestCodes.find(
                (c) => normalizeMac(c.receiver) === normalizedMonMac
            );

            const built = safeBuiltDetectors.find((b) => {
                if (b.mac_address !== mon.mac) return false;
                if (!b.code) return true;
                if (codeEntry && b.code === codeEntry.code) return true;
                return false;
            });

            const detBom = built ? safeDetectors.find((d) => d.id === built.detector) : undefined;

            const builtSubs = built?.subassemblies
                ?.map((subId) => safeBuiltSubAssemblies.find((s) => s.id === subId))
                .filter(Boolean) as BuiltSubAssemblies[];

            const subsBom = builtSubs?.map((bs) => {
                const sub = safeSubAssemblies.find((s) => s.id === bs.subassembly);
                return { ...bs, subassembly: sub };
            });

            return {
                monitoring: mon,
                code: codeEntry,
                built,
                detectorBom: detBom,
                builtSubAssemblies: builtSubs,
                subsBom,
            };
        });
    }, [detectorDataMonitoring, detectorCodeObj, builtDetectors, builtSubAssemblies, detectors, subAssemblies]);

    return {
        detectorDataMonitoring,
        detectorCodeObj,
        subAssemblies,
        detectors,
        builtDetectors,
        builtSubAssemblies,
        plannedLocations,
        fullDetectors,
        refreshDetectors,
        lastFetched,
    };
}